/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Flex, Input, SimpleGrid, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { gql, useLazyQuery } from '@apollo/client';
import BreadcrumbComponent from '../../../components/BreadcrumbComponent/BreadcrumbComponent';
import c from '../../../constant';

const QUERY_USAGE_BY_USER = gql`
	query getAiUsageByUser($fromDate: String!, $toDate: String!) {
		getAiUsageByUser(fromDate: $fromDate, toDate: $toDate) {
			userId
			username
			inputTokens
			outputTokens
			totalTokens
			callCount
		}
	}
`;

// Local calendar day as 'YYYY-MM-DD'. Built from the date parts rather than
// toISOString(), which converts to UTC first and so returns the previous day
// for anyone east of Greenwich — Cambodia is UTC+7, so that would be wrong for
// most of the working day.
function toDateInput(date) {
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatNumber(value) {
	return (value || 0).toLocaleString('en-US');
}

function AiUsage() {
	const { t } = useTranslation();
	const today = new Date();
	const monthAgo = new Date();
	monthAgo.setDate(monthAgo.getDate() - 29);

	const [fromDate, setFromDate] = useState(toDateInput(monthAgo));
	const [toDate, setToDate] = useState(toDateInput(today));
	const [loadUsage, { data, loading, error }] = useLazyQuery(QUERY_USAGE_BY_USER, { fetchPolicy: 'network-only' });

	useEffect(() => {
		window.scrollTo(0, 0);
		loadUsage({ variables: { fromDate, toDate } });
	}, []);

	const rows = data?.getAiUsageByUser || [];
	const totals = rows.reduce(
		(acc, row) => ({
			inputTokens: acc.inputTokens + (row.inputTokens || 0),
			outputTokens: acc.outputTokens + (row.outputTokens || 0),
			totalTokens: acc.totalTokens + (row.totalTokens || 0),
			callCount: acc.callCount + (row.callCount || 0),
		}),
		{ inputTokens: 0, outputTokens: 0, totalTokens: 0, callCount: 0 },
	);

	return (
		<Container maxW="container.xl" mt="16px" mb="16px">
			<BreadcrumbComponent
				list={[
					{ name: 'Settings', path: '/settings' },
					{ name: 'AI Token Usage', path: '#' },
				]}
			/>

			<Box mt="4" bg="white" borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="16px">
				<Flex gap="12px" align="flex-end" wrap="wrap">
					<Box>
						<Text fontSize="12px" color="gray.600" mb="4px">
							{t('From')}
						</Text>
						<Input type="date" size="sm" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} borderRadius={c.borderRadius} />
					</Box>
					<Box>
						<Text fontSize="12px" color="gray.600" mb="4px">
							{t('To')}
						</Text>
						<Input type="date" size="sm" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} borderRadius={c.borderRadius} />
					</Box>
					<Button
						size="sm"
						colorScheme="green"
						borderRadius={c.borderRadius}
						isLoading={loading}
						onClick={() => loadUsage({ variables: { fromDate, toDate } })}
					>
						{t('Search')}
					</Button>
				</Flex>
			</Box>

			{!loading && rows.length > 0 && (
				<SimpleGrid columns={[2, null, 4]} spacing="4" mt="4">
					{[
						{ label: 'Input tokens', value: totals.inputTokens },
						{ label: 'Output tokens', value: totals.outputTokens },
						{ label: 'Total tokens', value: totals.totalTokens },
						{ label: 'Model calls', value: totals.callCount },
					].map((tile) => (
						<Box key={tile.label} bg="white" borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="12px">
							<Text fontSize="11px" color="gray.500" textTransform="uppercase">
								{t(tile.label)}
							</Text>
							<Text fontSize="20px" fontWeight="bold">
								{formatNumber(tile.value)}
							</Text>
						</Box>
					))}
				</SimpleGrid>
			)}

			<Box mt="4" bg="white" borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="16px" overflowX="auto">
				{loading && (
					<Flex justify="center" py="24px">
						<Spinner />
					</Flex>
				)}

				{!loading && error && (
					<Text fontSize="13px" color="red.600">
						{t('Could not load usage')}
					</Text>
				)}

				{!loading && !error && rows.length === 0 && (
					<Text fontSize="13px" color="gray.500">
						{t('No AI usage in this period')}
					</Text>
				)}

				{!loading && !error && rows.length > 0 && (
					<Table size="sm">
						<Thead>
							<Tr>
								<Th>{t('User')}</Th>
								<Th isNumeric>{t('Input tokens')}</Th>
								<Th isNumeric>{t('Output tokens')}</Th>
								<Th isNumeric>{t('Total tokens')}</Th>
								<Th isNumeric>{t('Model calls')}</Th>
							</Tr>
						</Thead>
						<Tbody>
							{rows.map((row) => (
								<Tr key={row.userId || 'unattributed'} _hover={{ bg: 'gray.50' }}>
									<Td>{row.username || t('Unattributed')}</Td>
									<Td isNumeric>{formatNumber(row.inputTokens)}</Td>
									<Td isNumeric>{formatNumber(row.outputTokens)}</Td>
									<Td isNumeric fontWeight="600">
										{formatNumber(row.totalTokens)}
									</Td>
									<Td isNumeric>{formatNumber(row.callCount)}</Td>
								</Tr>
							))}
						</Tbody>
					</Table>
				)}
			</Box>
		</Container>
	);
}

export default AiUsage;
